import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { analytics } from '@/app/parts/Analytics';

import { sendMsg } from '@/lib/api/postMessageAPI';
import { mobx } from '@/lib/mobx';
import { isMobile } from '@/helpers/checkDeviceSize';
import { getLanguageFromLocation } from '@/helpers/getLanguageFromLocation';
import { getISOLang } from '@/config/constants';

const language = getISOLang(getLanguageFromLocation());

/**
 * TODO: this is a temporary function for testing purposes. Remove in future
 */
const getAnalyticsState = () =>
  [
    analytics.gtmLoaded ? '1' : '0',
    // biome-ignore lint/complexity/useLiteralKeys: we need this for TS
    analytics['isInitialized'] ? '1' : '0',
    // biome-ignore lint/complexity/useLiteralKeys: we need this for TS
    analytics['hasBeenSetUp'] ? '1' : '0',
  ].join('');

export const createHttpClient = ({ baseURL, handleError, isWithoutAuth = false }: ClientConfig) => {
  if (!baseURL) {
    throw new Error('baseURL is required');
  }

  const client = axios.create({ baseURL });

  client.interceptors.request.use(async (request: InternalAxiosRequestConfig) => {
    request.headers.Accept = 'application/json';
    request.headers['Content-Type'] = 'application/json';

    if (!isWithoutAuth) {
      const token = await mobx.profile.getToken();

      // adding locale to request
      if (request.method === 'post' && !request.url?.includes('cashout')) {
        if (!request.data) {
          request.data = {};
        }
        request.data.locale = mobx.ui.language || language;
      }

      // updating request url with language
      if (
        request.method === 'get' &&
        !request.url?.split('/').includes('rankings') &&
        !request.url?.includes('history') &&
        !request.url?.includes('/placebet/results') &&
        !request.url?.includes('cashout') &&
        !request.url?.includes('/intervals')
      ) {
        const params = request.params || {};
        params.locale = mobx.ui.language || language;
        request.params = params;
      }

      request.headers.Authorization = `Bearer ${token}`;

      if (['/placebet/ordinary', '/placebet/express'].includes(String(request.url))) {
        request.headers['x-envdt'] = getAnalyticsState();
        // request.url += `?envdt=${getAnalyticsState()}`;
      }

      if (!request.url?.includes('/history/tickets')) {
        request.headers['x-channel'] = isMobile() ? 'mobile' : 'desktop';

        const { playerId } = mobx.profile.user;

        if (playerId) {
          request.headers['X-Account'] = playerId;
        }
      }
    }

    return request;
  });

  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError<{ code: number }>) => {
      if (error.response) {
        const {
          data: { code },
          status,
        } = error.response;

        // if token is invalid or user not found or token expired
        if (status === 409 && [1, 2].includes(code)) {
          mobx.ui.setAlert({
            timeout: 10000,
            title: 'ES.COMMON.SESSION_EXPIRED',
            type: 'error',
          });

          sendMsg('session', {
            reason: error.message,
            status: 'error',
          });

          mobx.auth.logout();
        } else if (status === 401 && !error.config?._authRetry) {
          // unauthorized error means that we potentially have bad token
          // let's try to get a fresh one and retry but only once
          if (error.config) {
            error.config._authRetry = true;
          }

          mobx.profile.markTokenExpired();

          return client(error.config as AxiosRequestConfig);
        }
      }

      if (handleError) {
        return handleError(error);
      }

      return Promise.reject(error);
    },
  );

  return client;
};

/* Types */
interface ClientConfig extends Pick<AxiosRequestConfig, 'baseURL'> {
  handleError?: (error: AxiosError) => Promise<any>;
  isWithoutAuth?: boolean;
}
